/**
 * useComponentRegistry hook
 *
 * React hook for accessing component registry with automatic initialization
 */

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import registry, { isLoaded, getComponent, getAllTypes, getByCategory } from './ComponentRegistry';

/**
 * Hook to access component registry
 * Uses Redux state for loading status
 * @returns {Object} Registry state and methods
 */
export const useComponentRegistry = () => {
  const { isLoadingRegistry } = useSelector((state) => state.searchExperience);
  const loaded = isLoaded();

  return {
    registry: loaded ? registry.descriptors : null,
    loading: isLoadingRegistry,
    loaded,
    error: null,
  };
};

/**
 * Hook to get a specific component
 * @param {string} type - Component type
 * @returns {Object|null} Component info
 */
export const useComponent = (type) => {
  const [component, setComponent] = useState(null);

  useEffect(() => {
    if (isLoaded() && type) {
      const comp = getComponent(type);
      setComponent(comp);
    }
  }, [type]);

  return component;
};

/**
 * Hook to get all components
 * @returns {string[]} Array of component types
 */
export const useAllComponents = () => {
  const [components, setComponents] = useState([]);

  useEffect(() => {
    if (isLoaded()) {
      setComponents(getAllTypes());
    }
  }, []);

  return components;
};

/**
 * Hook to get components by category
 * @param {string} category - Component category
 * @returns {Object[]} Array of components
 */
export const useComponentsByCategory = (category) => {
  const [components, setComponents] = useState([]);

  useEffect(() => {
    if (isLoaded() && category) {
      setComponents(getByCategory(category));
    }
  }, [category]);

  return components;
};

export default useComponentRegistry;
